import { useRef, useState } from 'react';

import { Box, Flex, Image, useOutsideClick } from '@chakra-ui/react';
import { Add as AddIcon, Button } from '@mesh.ai/rabbit';
import { useToast } from 'components/hooks/useToast';
import { useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import emptyReviewPOA from 'assets/assetsV1/illustrations/deerIllustrations/emptyReviewPOA.svg';

import { TextBodyMedium, TextHeader3 } from 'uiToolkitV1/Typography';

import {
  CATEGORIES_REFERENCE_TYPE_MAP,
  CATEGORIES_TAG_MAP,
  REVIEW_ACTIONS,
  REVIEW_ACTION_CATEGORIES_OPTIONS,
} from '../constant';
import { addActionPlanItem } from '../hooks/dataHooks';
import { NewActionPlanItem } from '../types';
import { ActionAgendaInputWithCategory } from './ActionAgendaInputWithCategory';

type NoActionPlanProps = {
  reviewCycle: string;
  revieweeId: string;
  revieweeName: string;
  refetchActionPlanList: () => void;
};

export const NoActionPlan = ({ reviewCycle, revieweeId, revieweeName, refetchActionPlanList }: NoActionPlanProps) => {
  const [showAddAgendaTextField, setShowAddAgendaTextField] = useState(false);
  const [agendaText, setAgendaText] = useState('');
  const accessibleCategoryOption = Object.keys(REVIEW_ACTION_CATEGORIES_OPTIONS).find((key) =>
    REVIEW_ACTION_CATEGORIES_OPTIONS[key].hasAccess()
  ) as keyof typeof REVIEW_ACTION_CATEGORIES_OPTIONS | null;
  const [agendaCategory, setAgendaCategory] = useState<keyof typeof REVIEW_ACTION_CATEGORIES_OPTIONS>(
    accessibleCategoryOption ?? REVIEW_ACTIONS.GOAL
  );

  const inputContainerRef = useRef(null);
  const dropdownRef = useRef(null);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const toast = useToast();

  const addAgenda = async () => {
    try {
      const newActionPlanItem: NewActionPlanItem = {
        reviewActionPlanId: null,
        actionItemText: agendaText,
        tag: CATEGORIES_TAG_MAP[agendaCategory],
        isCompleted: false,
        actionReferenceId: null,
        referenceType: CATEGORIES_REFERENCE_TYPE_MAP[agendaCategory],
        isMlGenerated: false,
        requestId: null,
        fromEmployee: {
          id: loggedInEmployee.id,
        },
      };

      await addActionPlanItem({ newActionPlanItem, reviewCycle, revieweeId });
      refetchActionPlanList();

      toast({
        title: 'Action item created successfully',
        status: 'success',
      });
    } catch (error) {
      console.error('Error creating action plan item : ', error);

      toast({
        title: error?.message || 'An unknown error has occurred, please try again',
        status: 'error',
      });
    }
  };

  const saveAgenda = () => {
    if (agendaText.trim() !== '') addAgenda();
    setShowAddAgendaTextField(false);
    setAgendaText('');
    setAgendaCategory(REVIEW_ACTIONS.GOAL);
  };

  useOutsideClick({
    ref: inputContainerRef,
    handler: (e) => {
      const dropdownElement = dropdownRef.current as HTMLElement | null;
      if (showAddAgendaTextField && !dropdownElement?.contains(e.target as Node)) {
        saveAgenda();
      }
    },
  });

  if (showAddAgendaTextField) {
    return (
      <Box w="100%" h="calc(100vh - 146px)">
        <ActionAgendaInputWithCategory
          selectedCategory={agendaCategory}
          onCategoryChange={setAgendaCategory}
          dropdownRef={dropdownRef}
          containerRef={inputContainerRef}
          agendaText={agendaText}
          onChange={setAgendaText}
          onSubmit={saveAgenda}
        />
      </Box>
    );
  }

  return (
    <Flex flexDir="column" gap="16px" w="100%" h="calc(100vh - 146px)" alignItems="center" justifyContent="center">
      {/* @ts-expect-error src is not string */}
      <Image src={emptyReviewPOA} alt="No plan of action created" />
      <Box w="430px" textAlign="center">
        <TextHeader3 bold>No plan of action created</TextHeader3>
        <TextBodyMedium>{`You haven't added any actions for ${revieweeName} yet.`}</TextBodyMedium>
        <TextBodyMedium>{`Click on the "Add action plan" button to create your first one.`}</TextBodyMedium>
      </Box>
      <Button iconLeft={AddIcon} onClick={() => setShowAddAgendaTextField(true)}>
        Add action plan
      </Button>
    </Flex>
  );
};
